import type {
  IdentifyResult,
  IdentifyRequest,
  PriceData,
  Item,
  AddItemRequest,
  SearchResult,
} from '@shared/types';

const BASE = import.meta.env.VITE_API_BASE || '';

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let detail = text;
    try {
      const j = JSON.parse(text);
      detail = j.detail || j.error || text;
    } catch {}
    throw new Error(`${res.status}: ${detail || res.statusText}`);
  }
  return (await res.json()) as T;
}

export const api = {
  health: () => call<{ ok: boolean }>('/api/health'),

  whoami: () => call<{ email: string | null }>('/api/whoami'),

  identify: (body: IdentifyRequest) =>
    call<IdentifyResult>('/api/identify', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  priceLookup: (params: {
    game: 'pokemon' | 'lorcana';
    name: string;
    set?: string;
    number?: string;
    grade?: string;
    variant?: string;
  }) => {
    const q = new URLSearchParams();
    q.set('game', params.game);
    q.set('name', params.name);
    if (params.set) q.set('set', params.set);
    if (params.number) q.set('number', params.number);
    if (params.grade) q.set('grade', params.grade);
    if (params.variant) q.set('variant', params.variant);
    return call<PriceData>(`/api/price/lookup?${q.toString()}`);
  },

  listCollection: () => call<{ items: Item[] }>('/api/collection'),

  getItem: (id: string) => call<{ item: Item }>(`/api/collection/${id}`),

  addItem: (body: AddItemRequest) =>
    call<{ item: Item }>('/api/collection', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateItem: (id: string, body: Partial<Item>) =>
    call<{ ok: true }>(`/api/collection/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  deleteItem: (id: string) =>
    call<{ ok: true }>(`/api/collection/${id}`, { method: 'DELETE' }),

  search: (q: string) =>
    call<{ results: SearchResult[] }>(`/api/search?q=${encodeURIComponent(q)}`),

  getItemHistory: (id: string) =>
    call<{
      history: Array<{
        date: string;
        ebay_sold_median: number | null;
        tcgplayer_market: number | null;
      }>;
    }>(`/api/history/item/${id}`),

  getPortfolioHistory: () =>
    call<{ history: Array<{ date: string; total_value: number }> }>(
      '/api/history/portfolio',
    ),

  triggerSnapshot: () =>
    call<{
      total: number;
      succeeded: number;
      failed: number;
      skipped: number;
      errors: string[];
    }>('/api/snapshot', { method: 'POST' }),
};
